import { useLazyQuery } from '@apollo/client/react';
import { BlobServiceClient } from '@azure/storage-blob';
import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { ORGANISATION_OBJECT_LIBRARY_UPLOAD_SAS } from '../../graphql/objectLibrary';
import {
  useObjectDefinitionAttachAssetMutation,
  type ObjectDefinitionAssetKind,
  type OrganisationObjectLibraryUploadSasQuery,
  type OrganisationObjectLibraryUploadSasQueryVariables,
} from '../../types/graphqlTypes';

export type UploadedObjectAsset = { id: string; url: string; originalName: string };

/**
 * Upload a file to the organisation's container under a definition's
 * folder (`<definitionId>/<uuid>.<ext>`, the only names the server accepts)
 * and record it as an asset of that definition — for any definition
 * (importing a file creates the definition first).
 */
export function useObjectAssetUploader(organisationId: string) {
  const [requestSas] = useLazyQuery<OrganisationObjectLibraryUploadSasQuery, OrganisationObjectLibraryUploadSasQueryVariables>(ORGANISATION_OBJECT_LIBRARY_UPLOAD_SAS, { fetchPolicy: 'network-only' });
  // The definition's asset list (with read URLs) must include the new one.
  const [attachAsset] = useObjectDefinitionAttachAssetMutation({ refetchQueries: ['objectDefinition'] });

  return useCallback(
    async (definitionId: string, data: Blob, originalName: string, kind: ObjectDefinitionAssetKind, extension: string): Promise<UploadedObjectAsset> => {
      const sas = (await requestSas({ variables: { organisationId } })).data?.organisationObjectLibraryUploadSAS;
      if (!sas) throw new Error('no upload access');
      const blobName = `${definitionId}/${uuid()}.${extension.replace(/[^a-z0-9]/gi, '').slice(0, 8) || 'bin'}`;
      await new BlobServiceClient(sas)
        .getContainerClient('')
        .getBlockBlobClient(blobName)
        .uploadData(data, { blobHTTPHeaders: { blobContentType: data.type || 'application/octet-stream' } });
      const result = await attachAsset({ variables: { organisationId, definitionId, kind, blobName, originalName } });
      const asset = result.data?.objectDefinitionAttachAsset;
      if (!asset) throw new Error('asset not recorded');
      return { id: asset.id, url: asset.url, originalName: asset.originalName };
    },
    [requestSas, attachAsset, organisationId],
  );
}

/** {@link useObjectAssetUploader} for one definition (the editor's). */
export function useUploadObjectAsset(organisationId: string, definitionId: string) {
  const upload = useObjectAssetUploader(organisationId);
  return useCallback((data: Blob, originalName: string, kind: ObjectDefinitionAssetKind, extension: string) => upload(definitionId, data, originalName, kind, extension), [upload, definitionId]);
}
